import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  closestCenter,
  DragOverlay,
} from '@dnd-kit/core';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Globe,
  Smartphone,
  Link2,
  GripVertical,
  Bot,
  ShieldAlert,
  Archive,
  PackageCheck,
  PackageX,
  Loader2,
  PackagePlus,
  Activity,
} from 'lucide-react';
import { toast } from 'sonner';
import { relativeTime } from '@/lib/utils';

const PLATFORM_ICONS = {
  google: Globe,
  reddit: Smartphone,
  reddit_via_google: Link2,
};

const STATUS_META = {
  fresh:    { label: 'Fresh',    icon: PackagePlus,  hex: '#10b981',
              hint: 'Just minted — ready to claim onto an operative.' },
  in_use:   { label: 'In use',   icon: PackageCheck, hex: '#f59e0b',
              hint: 'Active on an operative right now.' },
  banned:   { label: 'Banned',   icon: PackageX,     hex: '#ef4444',
              hint: 'Tripped the platform — quarantine, do not reuse.' },
  archived: { label: 'Archived', icon: Archive,      hex: '#71717a',
              hint: 'Soft-deleted. R2 blob retained for forensics.' },
};
const STATUS_KEYS = ['fresh', 'in_use', 'banned', 'archived'];

// --------------------------------------------------------------------------
// Drag-and-drop primitives
// --------------------------------------------------------------------------

function IdentityCard({ row, dragging = false }) {
  const Icon = PLATFORM_ICONS[row.platform] || Globe;
  const label = row.email || row.username || `id ${row.id.slice(0, 8)}…`;
  const status = STATUS_META[row.status] || STATUS_META.archived;
  return (
    <div
      className={`group flex items-start gap-2 rounded-md border p-2.5 text-left transition ${
        dragging
          ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5 shadow-lg ring-2 ring-[hsl(var(--primary))]'
          : 'border-white/10 bg-[hsl(var(--surface-1))] hover:border-white/30 hover:bg-[hsl(var(--surface-2))]'
      }`}
      data-testid={`marketplace-card-${row.id}`}
    >
      <GripVertical className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--text-3))] opacity-50 group-hover:opacity-100" />
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: status.hex }} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium text-[hsl(var(--text-1))]" title={label}>
          {label}
        </div>
        <div className="mt-1 flex items-center gap-1.5 text-[10px] text-[hsl(var(--text-3))]">
          <span className="rounded bg-white/5 px-1 py-px font-mono uppercase tracking-wide">
            {row.platform.replace('_via_', '·')}
          </span>
          {row.created_via_pad_code && (
            <span className="truncate" title={`pad ${row.created_via_pad_code}`}>
              · {row.created_via_pad_code.slice(-6)}
            </span>
          )}
          {row.last_used_at && (
            <span title={new Date(row.last_used_at).toISOString()}>
              · {relativeTime(row.last_used_at)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function DraggableCard({ row }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `id:${row.id}`,
    data: { kind: 'identity', row },
  });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ opacity: isDragging ? 0.4 : 1, cursor: isDragging ? 'grabbing' : 'grab' }}
      data-testid={`marketplace-draggable-${row.id}`}
    >
      <IdentityCard row={row} />
    </div>
  );
}

function StatusColumn({ status, items, count }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `col:${status}`,
    data: { kind: 'status', status },
  });
  const meta = STATUS_META[status];
  const Icon = meta.icon;
  return (
    <div
      ref={setNodeRef}
      className={`flex h-full min-h-[60vh] flex-col rounded-lg border-2 transition ${
        isOver
          ? 'border-dashed border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5'
          : 'border-white/5 bg-[hsl(var(--surface-2))]/50'
      }`}
      data-testid={`marketplace-col-${status}`}
    >
      <div className="flex items-center justify-between border-b border-white/10 px-3 py-2.5">
        <div className="flex items-center gap-2">
          <Icon className="h-3.5 w-3.5" style={{ color: meta.hex }} />
          <span className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--text-1))]">
            {meta.label}
          </span>
        </div>
        <Badge variant="outline" className="font-mono tabular-nums">
          {count}
        </Badge>
      </div>
      <div className="flex-1 space-y-1.5 overflow-y-auto p-2">
        {items.length === 0 && (
          <div className="flex h-full min-h-[12rem] items-center justify-center text-center text-[10px] italic text-[hsl(var(--text-3))]">
            {meta.hint}
          </div>
        )}
        {items.map((row) => <DraggableCard key={row.id} row={row} />)}
      </div>
    </div>
  );
}

function OperativeDrop({ agent }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `agent:${agent.id}`,
    data: { kind: 'agent', agent },
  });
  return (
    <div
      ref={setNodeRef}
      className={`flex items-center gap-2 rounded-md border p-2.5 transition ${
        isOver
          ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 ring-2 ring-[hsl(var(--primary))]'
          : 'border-white/10 bg-[hsl(var(--surface-1))] hover:border-white/30'
      }`}
      data-testid={`marketplace-agent-${agent.id}`}
    >
      <Bot className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium text-[hsl(var(--text-1))]">
          {agent.name}
        </div>
        <div className="text-[10px] text-[hsl(var(--text-3))]">
          {agent.runtime || 'browser'} · {agent.status}
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Ban Sweep dialog — Phase Q.2.4 health-check tool
// --------------------------------------------------------------------------

function BanSweepDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const [platform, setPlatform] = React.useState('any');
  const [olderDays, setOlderDays] = React.useState(7);
  const [onlyInUse, setOnlyInUse] = React.useState(true);
  const [maxCount, setMaxCount] = React.useState(20);

  const sweepMut = useMutation({
    mutationFn: (body) =>
      api.post('/identities/ban-sweep', body).then((r) => r.data),
    onSuccess: (data) => {
      if (data.submitted > 0) {
        toast.success(`Queued ${data.submitted} ban sweeps — see Tasks for live progress`);
      } else {
        toast.info(data.notes || 'No identities matched');
      }
      qc.invalidateQueries({ queryKey: ['identities'] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Ban sweep failed'),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Ban Sweep</DialogTitle>
          <DialogDescription>
            Replay each matching identity's cookies in a stealth browser and
            classify the home page. Confirmed bans get auto-flipped to
            <span className="ml-1 rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] uppercase text-red-400">
              banned
            </span>. Catches silent shadow-bans before operators waste karma cycles.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-wide text-[hsl(var(--text-3))]">
              Platform
            </label>
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger data-testid="ban-sweep-platform">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any platform</SelectItem>
                <SelectItem value="reddit">Reddit</SelectItem>
                <SelectItem value="reddit_via_google">Reddit (Google-linked)</SelectItem>
                <SelectItem value="google">Google</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-wide text-[hsl(var(--text-3))]">
                Idle days ≥
              </label>
              <input
                type="number"
                min={0}
                max={365}
                value={olderDays}
                onChange={(e) => setOlderDays(Math.max(0, +e.target.value || 0))}
                className="h-9 w-full rounded-md border border-white/10 bg-[hsl(var(--surface-1))] px-2 text-sm"
                data-testid="ban-sweep-older-days"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-wide text-[hsl(var(--text-3))]">
                Max sweeps
              </label>
              <input
                type="number"
                min={1}
                max={100}
                value={maxCount}
                onChange={(e) => setMaxCount(Math.max(1, Math.min(100, +e.target.value || 1)))}
                className="h-9 w-full rounded-md border border-white/10 bg-[hsl(var(--surface-1))] px-2 text-sm"
                data-testid="ban-sweep-max-count"
              />
            </div>
          </div>

          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-white/10 bg-[hsl(var(--surface-1))] px-2.5 py-2">
            <input
              type="checkbox"
              checked={onlyInUse}
              onChange={(e) => setOnlyInUse(e.target.checked)}
              data-testid="ban-sweep-only-in-use"
            />
            <span className="text-xs">
              Only sweep <span className="font-mono">in_use</span> identities
              <span className="ml-1 text-[hsl(var(--text-3))]">
                (most likely to be silently banned)
              </span>
            </span>
          </label>

          <div className="rounded-md border border-amber-500/20 bg-amber-500/5 p-2.5 text-[11px] text-amber-300/90">
            Each sweep launches a stealth browser → ~30-60s + a Brain vision
            call (~$0.005). 20 sweeps = ~$0.10 + 10 min wall time.
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() =>
              sweepMut.mutate({
                platform: platform === 'any' ? null : platform,
                older_than_days: olderDays || null,
                only_in_use: onlyInUse,
                max_count: maxCount,
              })
            }
            disabled={sweepMut.isPending}
            data-testid="ban-sweep-submit"
          >
            {sweepMut.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
            Start sweep
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --------------------------------------------------------------------------
// Bulk-claim dialog (multi-select fresh accounts → drop on operative)
// --------------------------------------------------------------------------

function BulkClaimDialog({ open, onOpenChange, freshIdentities, agents, onClaim }) {
  const [agentId, setAgentId] = React.useState('');
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [count, setCount] = React.useState(5);

  React.useEffect(() => {
    if (!open) {
      setSelectedIds([]);
      setAgentId('');
    }
  }, [open]);

  const pickFirstN = () => {
    setSelectedIds(freshIdentities.slice(0, count).map((i) => i.id));
  };
  const toggle = (id) => {
    setSelectedIds((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Bulk-claim identities</DialogTitle>
          <DialogDescription>
            Hand a swarm of fresh accounts to one operative in a single shot.
            Banned, archived, and already-claimed entries are skipped.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-wide text-[hsl(var(--text-3))]">
              Operative
            </label>
            <Select value={agentId} onValueChange={setAgentId}>
              <SelectTrigger data-testid="bulk-claim-agent-select">
                <SelectValue placeholder="Select an operative…" />
              </SelectTrigger>
              <SelectContent>
                {agents.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name} <span className="text-[hsl(var(--text-3))]">({a.runtime || 'browser'} · {a.status})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1.5">
              <label className="text-xs uppercase tracking-wide text-[hsl(var(--text-3))]">
                Quick-pick first N fresh
              </label>
              <input
                type="number"
                min={1}
                max={Math.max(1, freshIdentities.length)}
                value={count}
                onChange={(e) => setCount(Math.max(1, Math.min(freshIdentities.length, +e.target.value || 1)))}
                className="h-9 w-full rounded-md border border-white/10 bg-[hsl(var(--surface-1))] px-2 text-sm"
                data-testid="bulk-claim-count-input"
              />
            </div>
            <Button onClick={pickFirstN} variant="secondary" disabled={!freshIdentities.length}
                     data-testid="bulk-claim-pick-first-n">
              Select first {Math.min(count, freshIdentities.length)}
            </Button>
          </div>

          <div className="max-h-64 space-y-1.5 overflow-y-auto rounded-md border border-white/10 bg-[hsl(var(--surface-1))] p-2">
            {freshIdentities.length === 0 && (
              <div className="p-3 text-center text-xs text-[hsl(var(--text-3))]">
                No fresh identities available. Run <code>signup_at_scale_mobile</code> first.
              </div>
            )}
            {freshIdentities.map((row) => (
              <label
                key={row.id}
                className="flex cursor-pointer items-start gap-2 rounded p-1.5 hover:bg-white/5"
              >
                <input
                  type="checkbox"
                  checked={selectedIds.includes(row.id)}
                  onChange={() => toggle(row.id)}
                  className="mt-0.5"
                  data-testid={`bulk-claim-row-${row.id}`}
                />
                <div className="flex-1">
                  <IdentityCard row={row} />
                </div>
              </label>
            ))}
          </div>

          <div className="text-xs text-[hsl(var(--text-3))]">
            {selectedIds.length} selected of {freshIdentities.length} fresh available
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => onClaim(selectedIds, agentId)}
            disabled={!agentId || selectedIds.length === 0}
            data-testid="bulk-claim-submit"
          >
            Claim {selectedIds.length} → operative
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --------------------------------------------------------------------------
// Main board
// --------------------------------------------------------------------------

export default function IdentityMarketplace({ identities, agents, isLoading }) {
  const qc = useQueryClient();
  const [activeRow, setActiveRow] = React.useState(null);
  const [bulkOpen, setBulkOpen] = React.useState(false);
  const [sweepOpen, setSweepOpen] = React.useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  const moveMut = useMutation({
    mutationFn: ({ id, status, agent_id }) =>
      api.post(`/identities/${id}/move`, { status, agent_id }).then((r) => r.data),
    onMutate: ({ id, status, agent_id }) => {
      // Optimistic — patch the cache so the card instantly snaps to its
      // new column without waiting on the network round-trip.
      const prev = qc.getQueryData(['identities', 'all', 'all']);
      qc.setQueriesData({ queryKey: ['identities'] }, (old) => {
        if (!old?.items) return old;
        return {
          ...old,
          items: old.items.map((r) =>
            r.id === id
              ? { ...r, status, last_used_agent_id: agent_id || null }
              : r,
          ),
        };
      });
      return { prev };
    },
    onError: (e, _vars, ctx) => {
      toast.error(e?.response?.data?.detail || 'Move failed');
      qc.invalidateQueries({ queryKey: ['identities'] });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['identities'] }),
  });
  const bulkClaimMut = useMutation({
    mutationFn: ({ identity_ids, agent_id }) =>
      api.post('/identities/bulk-claim', { identity_ids, agent_id }).then((r) => r.data),
    onSuccess: (data) => {
      toast.success(`Claimed ${data.claimed_count} identities (skipped ${data.skipped_count})`);
      qc.invalidateQueries({ queryKey: ['identities'] });
      setBulkOpen(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Bulk claim failed'),
  });

  const handleDragStart = (event) => {
    const data = event.active.data.current;
    if (data?.kind === 'identity') setActiveRow(data.row);
  };
  const handleDragEnd = (event) => {
    setActiveRow(null);
    const { active, over } = event;
    if (!over) return;
    const dragData = active.data.current;
    const dropData = over.data.current;
    if (dragData?.kind !== 'identity') return;
    const row = dragData.row;
    if (dropData?.kind === 'status') {
      if (dropData.status === row.status) return;
      if (dropData.status === 'in_use') {
        toast.info("Drop on an operative card to claim — status columns can only un-claim.");
        return;
      }
      moveMut.mutate({ id: row.id, status: dropData.status });
    } else if (dropData?.kind === 'agent') {
      if (row.status === 'in_use' && row.last_used_agent_id === dropData.agent.id) {
        return; // already claimed by this agent
      }
      if (row.status === 'banned' || row.status === 'archived') {
        toast.error(`Identity is ${row.status} — cannot claim`);
        return;
      }
      moveMut.mutate({ id: row.id, status: 'in_use', agent_id: dropData.agent.id });
    }
  };

  const buckets = React.useMemo(() => {
    const out = { fresh: [], in_use: [], banned: [], archived: [] };
    (identities || []).forEach((row) => {
      if (out[row.status]) out[row.status].push(row);
    });
    return out;
  }, [identities]);

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter}
                onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="space-y-4" data-testid="marketplace-root">
        <div className="flex items-center justify-between">
          <p className="text-xs text-[hsl(var(--text-3))]">
            Drag any card to a different status column to <span className="text-[hsl(var(--text-1))]">re-classify</span>,
            or onto an operative to <span className="text-[hsl(var(--text-1))]">claim</span> it.
          </p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => setBulkOpen(true)}
                     disabled={!buckets.fresh.length}
                     data-testid="marketplace-bulk-claim-btn">
              <PackageCheck className="mr-1 h-3.5 w-3.5" />
              Bulk-claim {buckets.fresh.length} fresh
            </Button>
            <Button size="sm" variant="outline" onClick={() => setSweepOpen(true)}
                     data-testid="marketplace-ban-sweep-btn"
                     className="border-amber-500/30 text-amber-300 hover:bg-amber-500/10 hover:text-amber-200">
              <Activity className="mr-1 h-3.5 w-3.5" />
              Ban sweep
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_1fr_1fr_1fr_280px]">
          {STATUS_KEYS.map((s) => (
            <StatusColumn
              key={s}
              status={s}
              items={buckets[s]}
              count={buckets[s].length}
            />
          ))}

          <div className="rounded-lg border-2 border-white/5 bg-[hsl(var(--surface-2))]/50">
            <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2.5">
              <Bot className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />
              <span className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--text-1))]">
                Operatives
              </span>
              <Badge variant="outline" className="ml-auto font-mono tabular-nums">
                {(agents || []).length}
              </Badge>
            </div>
            <div className="max-h-[60vh] space-y-1.5 overflow-y-auto p-2">
              {isLoading && (
                <div className="p-4 text-center text-xs text-[hsl(var(--text-3))]">
                  <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
                </div>
              )}
              {!isLoading && (agents || []).length === 0 && (
                <div className="p-4 text-center text-[10px] italic text-[hsl(var(--text-3))]">
                  No active operatives — create one on the Agents page.
                </div>
              )}
              {(agents || []).map((a) => <OperativeDrop key={a.id} agent={a} />)}
            </div>
          </div>
        </div>
      </div>

      <DragOverlay dropAnimation={null}>
        {activeRow ? <IdentityCard row={activeRow} dragging /> : null}
      </DragOverlay>

      <BulkClaimDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        freshIdentities={buckets.fresh}
        agents={agents || []}
        onClaim={(ids, agent_id) =>
          bulkClaimMut.mutate({ identity_ids: ids, agent_id })
        }
      />
      <BanSweepDialog open={sweepOpen} onOpenChange={setSweepOpen} />
    </DndContext>
  );
}
